import type { PointerEvent as ReactPointerEvent } from "react";
import { cn } from "@/shared/lib/utils";
import type { CellAbsence } from "./useAbsenceTooltip";

export function GridCell({
  dayIso,
  absence,
  editable,
  onEditOwn,
  onCreateOwn,
  onShow,
  selected,
  onPointerDown,
  onPointerEnter,
  onPointerUp,
}: {
  dayIso: string;
  absence: CellAbsence | null;
  // PlayerRow.isMe — only own absences are editable.
  editable: boolean;
  onEditOwn: (a: CellAbsence) => void;
  onCreateOwn: (dayIso: string) => void;
  onShow: (a: CellAbsence, x: number, y: number) => void;
  selected: boolean;
  onPointerDown: (
    event: ReactPointerEvent<HTMLButtonElement>,
    dayIso: string,
  ) => void;
  onPointerEnter: (
    event: ReactPointerEvent<HTMLButtonElement>,
    dayIso: string,
  ) => void;
  onPointerUp: (
    event: ReactPointerEvent<HTMLButtonElement>,
    dayIso: string,
  ) => void;
}) {
  // w-10 (40px) must stay = DAY_COLUMN_PX; useVisibleDayCount's fit math depends on it.
  if (!absence) {
    // Click opens the register-absence dialog preselected to this day.
    if (editable) {
      return (
        <button
          type="button"
          title={dayIso}
          aria-label={`${dayIso}: närvarande – registrera frånvaro`}
          onClick={() => onCreateOwn(dayIso)}
          onPointerDown={(event) => onPointerDown(event, dayIso)}
          onPointerEnter={(event) => onPointerEnter(event, dayIso)}
          onPointerUp={(event) => onPointerUp(event, dayIso)}
          // Translucent --foreground wash, not an opaque fill: a solid grey smudged the blue raid tint.
          className={cn(
            "block h-9 w-10 border border-border/40 hover:bg-foreground/15",
            selected && "bg-primary/25 hover:bg-primary/25",
          )}
        />
      );
    }
    return (
      <div
        role="img"
        title={dayIso}
        aria-label={`${dayIso}: närvarande`}
        className="h-9 w-10 border border-border/40"
      />
    );
  }
  const label = `${dayIso}: ${absence.reason} (${absence.startDate} till ${absence.endDate})`;
  return (
    // `data-absence-cell`: the document mouseover listener hides the tooltip when the pointer leaves marked cells.
    <button
      type="button"
      data-absence-cell
      aria-label={label}
      aria-disabled={!editable}
      onClick={() => editable && onEditOwn(absence)}
      onPointerDown={(event) => editable && onPointerDown(event, dayIso)}
      onPointerEnter={(event) => editable && onPointerEnter(event, dayIso)}
      onPointerUp={(event) => editable && onPointerUp(event, dayIso)}
      onMouseMove={(e) => onShow(absence, e.clientX, e.clientY)}
      // Red sits inset (p-[3px]) so it reads as a rounded pill inside the grid-line border.
      className="group block h-9 w-10 border border-border/40 p-[3px]"
    >
      {/* Red always, regardless of theme — a status color, not a semantic token (raw-color use allowed per CLAUDE.md). */}
      <div
        className={cn(
          "h-full w-full rounded-[4px] bg-[oklch(0.55_0.16_25)]",
          editable && "group-hover:bg-[oklch(0.6_0.17_25)]",
        )}
      />
    </button>
  );
}
